import { useCallback, useMemo, useState } from "react";
import { Grid, CardContent, Button, Icon, styled } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
// Components
import { FilterSearchTable } from "src/components/FilterSearchTable/FilterSearchTable";
// Redux
import { ITableColumn } from "src/components/FilterSearchTable/TableComponents/TableHeader";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { BookingErrorType, IBookingError } from "@vmosolutions/oc-interfaces";
import { DateTimeField } from "src/components/FormikComponents/DateTimeField";
import moment from "moment-timezone";
import { TextField } from "src/components/FormikComponents/TextField";
import { SelectField } from "src/components/FormikComponents/SelectField";
import _ from "lodash";

interface IManageProps {
  path?: string[];
  id?: string;
  tableMaxHeight?: number;
}

const OCManageBookingErrors = styled(CardContent)(({ theme }) => ({
  flexGrow: 1,
  height: "100%",
  position: "relative",
  "& .booking_errors_display_table": {
    maxWidth: 1450,
    "& .optional_controls": {
      width: "auto",
      flexFlow: "column",
      "& .search_items": {
        width: "auto",
        flexWrap: "nowrap",
        "& .date_selectors": {
          display: "flex",
          flexDirection: "row",
          flexWrap: "nowrap",
        },
      },
    },
    "& button.search_button": {
      margin: 3,
      padding: "2px 8px",
      fontSize: 12,
      fontWeight: 300,
      width: 100,
      "& .search_icon": { fontSize: 18, marginRight: 5 },
    },
  },
}));

// PAIRINGS
const bookingErrorsTableDesc = {
  tableTitle: "",
  tableDescription: "",
  noMessage: "No Booking Errors to display, use the filters above to search.",
};
const bookingErrorsTableInfo: ITableColumn[] = [
  {
    id: "_id",
    value: "_id",
    index: 0,
    numeric: true,
    label: "ID",
    type: "string",
    width: 10,
    visible: false,
    filter: false,
  },
  {
    id: "pairingComponent",
    value: "pairingComponent",
    index: 1,
    numeric: true,
    label: "Pairing Events",
    type: "object",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "component",
    value: "component",
    index: 2,
    numeric: true,
    label: "Booking",
    type: "object",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "associatedPairings",
    value: "associatedPairings",
    index: 3,
    numeric: true,
    label: "Pairings",
    type: "object",
    width: 10,
    visible: true,
    filter: false,
  },
];

// Function used to create the dataSet needed for the table component.
export const tableData: any = [
  {
    id: "bookingErrors",
    dataType: "bookingErrors",
    label: "Booking Errors",
    data: [],
    tableInfo: bookingErrorsTableInfo,
    tableInfoDisplay: bookingErrorsTableDesc,
  },
];

interface IErrorTypeOption {
  label: string;
  value: BookingErrorType;
}

const errorOptions: IErrorTypeOption[] = [
  { label: "All", value: BookingErrorType.ALL },
  { label: "Mismatch", value: BookingErrorType.BOOKING_PAIRING_MISMATCH },
  { label: "Over Booked", value: BookingErrorType.OVERBOOKED_FLIGHT },
  { label: "No Booking", value: BookingErrorType.UNBOOKED_FLIGHT },
];

const filterTableRows = (
  rows: IBookingError[],
  locator: string,
  selectedOption: IErrorTypeOption,
  shortCode: string
): IBookingError[] => {
  const filteredRows = rows.filter((row: IBookingError) => {
    const filter1 = locator ? row.locator?.toLowerCase()?.startsWith(locator.toLowerCase()) : true;
    const filter2 =
      selectedOption && selectedOption.value !== BookingErrorType.ALL
        ? row.pairingInfo?.length > 0
          ? row.pairingInfo.find((p) => p.errorType?.includes(selectedOption?.value))
          : row.errorType?.includes(selectedOption?.value)
        : true;
    const filter3 = shortCode
      ? row.associatedPairings?.find((pair) => pair?.shortId?.startsWith(shortCode.toUpperCase()))
      : true;
    return filter1 && filter2 && filter3;
  });
  return filteredRows;
};

/**
 * Manage Bookings Errors.
 */
export const ManageBookingErrors = (props: IManageProps): JSX.Element => {
  const { id, path, tableMaxHeight } = props;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<IBookingError[]>();

  const [shortCode, setShortCode] = useState<string>();
  const [selectedOption, setSelectedOption] = useState<IErrorTypeOption>(errorOptions?.[0]);
  const [locator, setLocator] = useState<string>();

  const [start, setStart] = useState<number>();
  const [end, setEnd] = useState<number>();

  const getErrors = useCallback(async () => {
    setIsLoading(true);
    const response: IBookingError[] = await AmplifyAPIWrapper.Get(`/bookings/error?start=${start}&end=${end}`);
    setData(response);
    setIsLoading(false);
  }, [start, end]);

  const debouncedSearchBookingErrors = _.debounce(async (): Promise<void> => {
    await getErrors();
  }, 1000);

  const filteredTableRows = useMemo(() => {
    return data ? filterTableRows(data, locator, selectedOption, shortCode) : [];
  }, [data, shortCode, selectedOption, locator]);

  const tableData = useMemo(() => {
    return [
      {
        id: "bookingErrors",
        dataType: "bookingErrors",
        label: "Booking Errors",
        data: filteredTableRows,
        tableInfo: bookingErrorsTableInfo,
        tableInfoDisplay: bookingErrorsTableDesc,
      },
    ];
  }, [filteredTableRows]);

  return (
    <OCManageBookingErrors>
      <Grid container direction="column">
        <Grid item>
          <FilterSearchTable
            id={id}
            path={path}
            className="booking_errors_display_table"
            events={tableData}
            uniqueKey={"_id"}
            allowMultiFilter={true}
            selectModeSingle={false}
            hideFilter={true}
            hideFocusSelected={true}
            hideSelectionCheckboxes={false}
            selectUsingCheckboxesOnly={true}
            stickyHeader={false}
            maxTableHeight={tableMaxHeight}
            showUTCLocalToggle={filteredTableRows?.length > 0 ? true : false}
            optionalControls={
              <Grid container className="optional_controls">
                <Grid container className="search_items">
                  <Grid item className="date_selectors">
                    <DateTimeField
                      className="start range_datetimefield"
                      name="event_start"
                      label={"Departure Date From"}
                      value={start}
                      // Always sets the start of this selected date (utc time)
                      onChange={(date) =>
                        date ? setStart(moment.utc(date).startOf("day")?.valueOf()) : setStart(null)
                      }
                      DateProps={{ maxDate: new Date(end - 86400000) }} // end - day.
                      isEpoch={true}
                      maxDate={end ? new Date(end)?.valueOf() : undefined}
                      disabled={isLoading}
                    />
                    <DateTimeField
                      className="end range_datetimefield"
                      name="event_end"
                      label={"Until"}
                      value={end}
                      // Always sets the end of this selected date (utc time)
                      onChange={(date) => (date ? setEnd(moment.utc(date).startOf("day")?.valueOf()) : setEnd(null))}
                      DateProps={{ minDate: new Date(start + 86400000) }} // start + day.
                      isEpoch={true}
                      minDate={start ? new Date(start)?.valueOf() : undefined}
                      disabled={isLoading}
                    />
                  </Grid>

                  <Grid item style={{ width: "auto" }}>
                    <Button
                      id={"search_btn"}
                      variant="outlined"
                      onClick={() => setData(null)}
                      disabled={isLoading || !start || !end || !data}
                      className="search_button"
                    >
                      Clear
                    </Button>
                    <Button
                      id={"search_btn"}
                      variant="outlined"
                      onClick={() => debouncedSearchBookingErrors()}
                      disabled={isLoading}
                      className="search_button"
                    >
                      {!isLoading ? (
                        <>
                          <Icon className="search_icon">{"search"}</Icon>
                          {"Search"}
                        </>
                      ) : (
                        <>
                          <CircularProgress color="inherit" style={{ width: 14, height: 14, marginRight: 8 }} />{" "}
                          searching
                        </>
                      )}
                    </Button>
                  </Grid>
                </Grid>

                {!!data && (
                  <Grid container style={{ width: "auto" }}>
                    <Grid item>
                      <SelectField
                        label="Filter Error Type"
                        className="error_type_filter"
                        name="bookings_ errors_type"
                        menuPosition="fixed"
                        options={errorOptions}
                        value={selectedOption}
                        onChange={setSelectedOption}
                      />
                    </Grid>

                    <Grid item>
                      <TextField
                        label="Filter Pairing"
                        className="shortcode_filter"
                        name="checkin_status_shortcode_filter"
                        value={shortCode}
                        onChange={(event: any) => {
                          const value = event.target.value ?? null;
                          setShortCode(value);
                        }}
                        type="text"
                        fullWidth={false}
                        inputProps={{ style: { textTransform: "uppercase" } }}
                      />
                    </Grid>
                    <Grid item>
                      <TextField
                        label="Filter Booking Locator"
                        className="booking_locator_filter"
                        name="booking_locator_filter"
                        value={locator}
                        onChange={(event: any) => {
                          const value = event.target.value ?? null;
                          setLocator(value);
                        }}
                        type="text"
                        fullWidth={false}
                      />
                    </Grid>
                  </Grid>
                )}
              </Grid>
            }
          />
        </Grid>
      </Grid>
    </OCManageBookingErrors>
  );
};
